/**
 * Colour helpers for task cards.
 *
 * Task colours are user-chosen (TASK_COLORS, and older tasks may carry any
 * arbitrary hex), so nothing here may assume a known palette. Every value is
 * derived from the hex at runtime and checked for contrast, so a pale yellow
 * and a deep indigo both stay readable in light *and* dark mode.
 */

interface RGB {
  r: number;
  g: number;
  b: number;
}

export const hexToRgb = (hex: string): RGB => {
  const clean = hex.replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean.split('').map((c) => c + c).join('')
      : clean.padEnd(6, '0').slice(0, 6);
  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
};

/** WCAG relative luminance (0 = black, 1 = white). */
export const luminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** WCAG contrast ratio between two hex colours (1–21). */
export const contrastRatio = (a: string, b: string): number => {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Pick black or white text for a solid background, whichever actually reads.
 * This is why a user picking bright yellow still gets legible cards.
 */
export const readableTextOn = (bgHex: string): string =>
  contrastRatio(bgHex, '#FFFFFF') >= contrastRatio(bgHex, '#111111') ? '#FFFFFF' : '#111111';

export const rgba = (hex: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Nudge a colour toward readability against a given ground. Very light colours
 * are darkened for light mode; very dark ones are lightened for dark mode, so
 * accents/labels never vanish into the card.
 */
export const adjustForTheme = (hex: string, isDark: boolean): string => {
  const { r, g, b } = hexToRgb(hex);
  const lum = luminance(hex);
  const shift = (v: number, amount: number) =>
    Math.max(0, Math.min(255, Math.round(v + amount)));

  if (isDark && lum < 0.12) {
    // Too dark to show on a near-black card — lift it.
    return `rgb(${shift(r, 70)}, ${shift(g, 70)}, ${shift(b, 70)})`;
  }
  if (!isDark && lum > 0.62) {
    // Too pale to show on white — deepen it.
    const factor = 0.62;
    return `rgb(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)})`;
  }
  return hex;
};

/**
 * The full tinted-card palette derived from one task colour.
 * The card is washed in the colour rather than marked with a thin strip, but
 * the wash stays low-alpha so body text keeps the theme's own contrast.
 */
export const taskCardTheme = (hex: string, isDark: boolean) => {
  const accent = adjustForTheme(hex || '#9333EA', isDark);
  return {
    accent,
    /** Card background wash. */
    background: rgba(hex || '#9333EA', isDark ? 0.16 : 0.09),
    /** Slightly stronger wash for hover. */
    backgroundHover: rgba(hex || '#9333EA', isDark ? 0.24 : 0.15),
    border: rgba(hex || '#9333EA', isDark ? 0.42 : 0.28),
    /** For a solid chip filled with the colour. */
    onAccent: readableTextOn(accent),
  };
};

/* ------------------------------------------------------------------ *
 * Legibility, computed rather than guessed.
 *
 * adjustForTheme above lifts a colour when its *luminance* crosses a fixed
 * threshold. That threshold turns out to be the wrong test: it passes plenty
 * of colours that still fail WCAG against the surface they land on. A measured
 * audit of every colour this app draws found 35 combinations below threshold,
 * the worst being the "In progress" status chip at 2.55:1 where 4.5:1 is
 * required, and fourteen marks -- task rails, habit bars, list borders -- under
 * the 3:1 floor for a non-text element.
 *
 * So these step a colour toward or away from the surface until it actually
 * measures up, rather than assuming a threshold stands in for the measurement.
 *
 * Note for later: taskColor.ts is mirrored in taskdojo-app. These additions are
 * web-only for now, so the two platforms tint marks slightly differently until
 * the same helpers are carried across. The stored values are untouched -- this
 * is entirely how a colour is drawn, never what is saved.
 * ------------------------------------------------------------------ */

/** WCAG non-text minimum: rails, bars, dots, borders that carry meaning. */
export const MARK_MIN = 3.0;
/** WCAG minimum for normal-size text. */
export const TEXT_MIN = 4.5;

/** Composite `hex` at `alpha` over `base` -- what a tinted chip really is. */
export const blend = (hex: string, base: string, alpha: number): string => {
  const f = hexToRgb(hex);
  const b = hexToRgb(base);
  const mix = (x: number, y: number) => Math.round(x * alpha + y * (1 - alpha));
  const two = (n: number) => n.toString(16).padStart(2, '0');
  return `#${two(mix(f.r, b.r))}${two(mix(f.g, b.g))}${two(mix(f.b, b.b))}`;
};

/**
 * Step `hex` lighter or darker until it clears `target` against `surface`.
 *
 * Direction is chosen by which side of the surface there is room on, so the
 * same call works on a dark card and a light one. Hue is preserved by scaling
 * the channels rather than desaturating toward white or black; if 24 steps are
 * not enough -- which only happens against a mid grey -- it returns the best it
 * reached rather than pretending.
 */
export const readableOn = (
  hex: string,
  surface: string,
  target: number = MARK_MIN,
): string => {
  if (contrastRatio(hex, surface) >= target) return hex;

  const surfaceIsDark = luminance(surface) < 0.35;
  const { r, g, b } = hexToRgb(hex);
  const two = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

  let best = hex;
  let bestRatio = contrastRatio(hex, surface);

  for (let i = 1; i <= 24; i += 1) {
    const t = i / 24;
    // Toward white on a dark surface, toward black on a light one.
    const candidate = surfaceIsDark
      ? `#${[r, g, b].map((c) => two(c + (255 - c) * t).toString(16).padStart(2, '0')).join('')}`
      : `#${[r, g, b].map((c) => two(c * (1 - t)).toString(16).padStart(2, '0')).join('')}`;
    const ratio = contrastRatio(candidate, surface);
    if (ratio > bestRatio) { best = candidate; bestRatio = ratio; }
    if (ratio >= target) return candidate;
  }
  return best;
};

/**
 * The text colour for a chip tinted with its own hue -- the pattern used for
 * task status, mood and connection status all over the app.
 *
 * The chip's real background is the hue composited over the card, so that is
 * what the text has to be measured against, not the card itself. Measuring
 * against the card is why these looked fine and read badly.
 */
export const chipText = (
  hex: string,
  card: string,
  alpha: number = 0.125,
): string => readableOn(hex, blend(hex, card, alpha), TEXT_MIN);
